import { all, fork, put, takeEvery, call, select, delay } from 'redux-saga/effects';
import { message as antdMessage } from 'antd';
import * as actionTypes from 'appRedux/actions/rsv/ReservationActionType';
import * as actionCreators from 'appRedux/actions/rsv/Reservation';
import { userSignOut } from 'appRedux/actions/Auth';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { getItems } from 'util/localStorage';
import { ApiResponse } from 'constants/ActionTypes';
import { APIGetRequest, APIPostRequest } from 'util/connection';
import { AppState } from 'ReducerState';
import { detailInitialState } from 'appRedux/reducers/rsv/Reservation';

const syncPrimaryGuestId = (guests: any[] | undefined, guestId?: number | string) => {
  if (!Array.isArray(guests) || guests.length === 0) return guests;
  if (guestId === undefined || guestId === null || guestId === '') return guests;
  return guests.map((guest, idx) => (idx === 0 ? { ...guest, id: guestId } : guest));
};

const formatApiDate = (value: any): string | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

const formatTimeHHmm = (t: any): string => {
  if (!t) return '00:00';
  if (dayjs.isDayjs(t)) return t.format('HH:mm');
  if (typeof t === 'string') return t.slice(0, 5);
  return '00:00';
};

const unwrapRpcResponseData = (responseData: any) => responseData?.data ?? responseData ?? {};

const normalizeGroupBookingDetails = (details: any[] | undefined) => {
  if (!Array.isArray(details)) return [];
  return details.map((detail) => {
    // Strip UI-only fields that must never reach the backend
    const {
      dateRange: _dr,
      rate_multiple: _rm,
      keep_original_rate: _kor,
      no_canel: noCanel,
      ...rest
    } = detail ?? {};
    return {
      ...rest,
      arrival: formatApiDate(rest?.arrival),
      departure: formatApiDate(rest?.departure),
      no_cancel: rest?.no_cancel ?? noCanel ?? false,
      rate_schedule: Array.isArray(rest?.rate_schedule)
        ? rest.rate_schedule.map((rate: any) => ({
          ...rate,
          date: formatApiDate(rate?.date),
        }))
        : [],
    };
  });
};

function* reservationRequestProcess(value: actionTypes.ReservationRequest) {
  const { payload, sorter = undefined } = value;

  let keywords = payload;
  const reservationInfo = {} as any;
  if (keywords.searchText) reservationInfo.search_text = keywords.searchText;
  if (keywords.arrival) reservationInfo.arrival = dayjs(keywords.arrival).format('YYYY-MM-DD');
  if (keywords.departure)
    reservationInfo.departure = dayjs(keywords.departure).format('YYYY-MM-DD');
  if (keywords.stayDate) reservationInfo.stay_date = dayjs(keywords.stayDate).format('YYYY-MM-DD');
  if (keywords.roomNumber) reservationInfo.room_number = keywords.roomNumber;
  if (keywords.roomTypeId) reservationInfo.roomtype_id = keywords.roomTypeId;
  if (keywords.channelName) reservationInfo.channel_name = keywords.channelName;
  if (keywords.vipId) reservationInfo.vip_id = keywords.vipId;
  if (keywords.memberCode) reservationInfo.member_code = keywords.memberCode;
  if (keywords.company) reservationInfo.company = keywords.company;
  if (keywords.nationId) reservationInfo.nation_id = keywords.nationId;
  if (keywords.ratePlanId) reservationInfo.rateplan_id = keywords.ratePlanId;
  if (keywords.registerId) reservationInfo.register_id = keywords.registerId;
  if (keywords.bookingId) reservationInfo.booking_id = keywords.bookingId;
  if (keywords.book_type) reservationInfo.book_type = keywords.book_type;

  const roomStatus = [];
  if (keywords.statusRSV) roomStatus.push('R');
  if (keywords.statusCFM) roomStatus.push('C');
  if (keywords.statusTTT) roomStatus.push('W');
  if (keywords.statusCXL) roomStatus.push('X');
  if (keywords.statusNOS) roomStatus.push('N');
  if (keywords.statusINH) roomStatus.push('I');
  if (keywords.statusCO) roomStatus.push('O');

  reservationInfo.statuses = isEmpty(roomStatus) ? 'R,C,W,X,N,I,O' : roomStatus.toString();

  reservationInfo.include_archive = !!keywords.includeArchive;

  /* Set paging with sort_field/sort_order inside paging */
  const pagingObj: any = { page: payload.current, limit: payload.limit };
  if (sorter !== undefined && sorter.fieldArray && sorter.fieldArray.length > 0 && sorter.orderArray && sorter.orderArray.length > 0) {
    pagingObj.sort_field = sorter.fieldArray[0];
    pagingObj.sort_order = sorter.orderArray[0];
  }
  reservationInfo.paging = pagingObj;

  const { access_token } = yield call(getItems, ['access_token']);
  const AccessToken = access_token;
  const url = `${API_URL_V1}pms/rpc/reservation/booking/search`;
  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

  try {
    const response: ApiResponse = yield APIPostRequest(url, reservationInfo, API_PMS_HEADERS);
    if (response.data?.result_code === '0') {
      const { items, paging } = response.data.result_data;
      /* if no error put everything to reducer */
      yield put(actionCreators.reservationDataSourceSuccess({ results: items, paging }));
      yield delay(100);
      yield put(
        actionCreators.reservationSetFilter({
          ...payload,
          current: paging?.current,
          limit: paging?.limit,
          pageSize: paging?.limit,
          total: paging?.total_rows,
        })
      );
      yield delay(100);
    } else {
      /* Show error message and signout */
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'reservationPreview', message };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.reservationProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'reservationPreview', message: error };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.reservationProcessReset());
    yield delay(100);
  }
}

function* reservationDetailRequestProcess({ payload }: actionTypes.ReservationDetailRequest) {
  const { access_token } = yield call(getItems, ['access_token']);
  const AccessToken = access_token;
  const url = `${API_URL_V1}pms/rpc/reservation/booking/get`;
  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
  const bookingId = typeof payload === 'object' ? (payload as any).booking_id : payload;

  try {
    const response: ApiResponse = yield APIPostRequest(url, {
      booking_id: bookingId,
    }, API_PMS_HEADERS);
    if (response.data?.result_code === '0') {
      const reservationDetail = response.data.result_data;
      /* if no error put everything to reducer */
      yield put(actionCreators.reservationDetailDataSourceSuccess(reservationDetail));
      yield delay(100);
      yield put(actionCreators.reservationBookingSummarySuccess(reservationDetail.summary));
      yield delay(100);
    } else {
      const { result_msg, message, data } = response.data;
      const errorMsg = result_msg || message || 'Failed to load reservation detail.';
      antdMessage.error(errorMsg);
      yield put(actionCreators.reservationDetailDataSourceSuccess(detailInitialState));
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    antdMessage.error(String(error) || 'Failed to load reservation detail.');
    yield put(actionCreators.reservationDetailDataSourceSuccess(detailInitialState));
    yield delay(100);
  }
}

function* reservationNewProcess({ payload }: actionTypes.ReservationNew) {
  const { bookingData, confirmStatus, confirmRemark } = payload;
  const arrival = dayjs(bookingData.arrival).format('YYYY-MM-DD');
  const departure = dayjs(bookingData.departure).format('YYYY-MM-DD');
  const arrival_time = formatTimeHHmm(bookingData.arrival_time);
  const departure_time = formatTimeHHmm(bookingData.departure_time);

  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    // Use group booking endpoint when book_type is 'G'
    const isGroupBooking = bookingData?.book_type === 'G';
    const url = isGroupBooking
      ? `${API_URL_V1}pms/rpc/reservation/group_booking/create`
      : `${API_URL_V1}pms/reservation/booking/create`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

    // For group bookings: Tour Order field value is sent as `remark` (not `tour_order`)
    let bookingPayload: any;
    if (isGroupBooking) {
      const { tour_order, ...restBookingData } = bookingData as any;
      bookingPayload = {
        ...restBookingData,
        arrival,
        departure,
        arrival_time,
        departure_time,
        remark: tour_order ?? null,
      };
    } else {
      bookingPayload = { ...bookingData, arrival, departure, arrival_time, departure_time };
    }

    const booking = {
      confirm: confirmStatus,
      confirm_remark: confirmRemark,
      booking: bookingPayload,
    };

    const API_DATABODY = booking;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg, result_data } = response.data;
      const processStatus = {
        status: true,
        action: 'createBooking',
        message: result_msg,
        result_code,
        result_data: result_data ?? null,
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.reservationProcessReset());
    } else {
      /* Show error message and signout */
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'createBooking', message };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.reservationProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'createBooking', message: error };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
    yield put(actionCreators.reservationProcessReset());
  }
}
function* reservationQuickNewProcess({ payload }: actionTypes.ReservationQuickNew) {
  try {
    // Optional: open_rate (for plan_type === 9)
    const normalizedOpenRate = payload?.open_rate
      ? {
        amount: Number(payload?.open_rate?.amount ?? 0),
        // allow explicit 0, otherwise null when missing
        mealtype_id:
          payload?.open_rate?.mealtype_id === 0 ? 0 : payload?.open_rate?.mealtype_id ?? null,
        extra_adult_rate: Number(payload?.open_rate?.extra_adult_rate ?? 0),
        extra_child_rate: Number(payload?.open_rate?.extra_child_rate ?? 0),
      }
      : undefined;

    const body = {
      room_id: Number(payload?.room_id),
      arrival: payload?.arrival ? dayjs(payload.arrival).format('YYYY-MM-DD') : null,
      departure: payload?.departure ? dayjs(payload.departure).format('YYYY-MM-DD') : null,
      reservation_type_id: payload?.reservation_type_id ?? null,
      channel_id: payload?.channel_id ?? null,
      voucher_no: payload?.voucher_no ?? null,
      origin_id: payload?.origin_id ?? null,
      adult: Number(payload?.adult ?? 1),
      child: Number(payload?.child ?? 0),
      infant: Number(payload?.infant ?? 0),
      extra_adult: Number(payload?.extra_adult ?? 0),
      extra_child: Number(payload?.extra_child ?? 0),
      extra_infant: Number(payload?.extra_infant ?? 0),
      no_amend: Boolean(payload?.no_amend ?? false),
      no_cancel: Boolean(payload?.no_cancel ?? false),
      no_refund: Boolean(payload?.no_refund ?? false),
      rate_id: payload?.rate_id ?? null,
      rateplan_id: payload?.rateplan_id ?? null,
      rate_schedule: Array.isArray(payload?.rate_schedule)
        ? payload.rate_schedule.map((r: any) => ({
          date: r?.date ? dayjs(r.date).format('YYYY-MM-DD') : null,
          amount: Number(r?.amount ?? 0),
          extra_adult_rate: Number(r?.extra_adult_rate ?? 0),
          extra_child_rate: Number(r?.extra_child_rate ?? 0),
          extra_infant_rate: Number(r?.extra_infant_rate ?? 0),
          comp: Boolean(r?.comp ?? false),
          mealtype_id: r?.mealtype_id ?? null,
        }))
        : [],
      ...(normalizedOpenRate ? { open_rate: normalizedOpenRate } : {}),
      guest: payload?.guest ?? null,
      note: typeof payload?.note === 'string' ? payload.note.trim() : payload?.note ?? null,
    };

    // Access token & headers
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/rpc/reservation/quick_booking`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

    // Call API
    const response: ApiResponse = yield APIPostRequest(url, body, API_PMS_HEADERS);

    if (response.status === 200) {
      const apiData = unwrapRpcResponseData(response.data);
      const { result_code, result_msg, msg_code, result_data } = apiData;
      const processStatus = {
        status: true,
        action: 'quickBooking',
        message: result_msg ?? 'Quick Booking success.',
        result_code,
        result_msg,
        msg_code,
        result_data,
        api_status: response.status,
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      // Notify listeners (e.g., RoomPlan) that a quick booking just succeeded
      try {
        yield put({ type: 'RSV/QUICK_BOOKING_SUCCEEDED' });
      } catch (e) {
        // non-fatal
      }
      // Do not reset here. RoomPlan can be busy rendering a large calendar and may
      // miss a short-lived process state. The quick-booking modal resets it after
      // showing the user-facing success/error dialog.
    } else {
      const { message, data } = response.data || { message: 'Unknown error' };
      const processStatus = { status: false, action: 'quickBooking', message };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      if (response.status === 403 && data) {
        yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'quickBooking', message: error };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
  }
}
function* reservationUpdateProcess({ payload }: actionTypes.ReservationUpdate) {
  const { bookingData, confirmStatus, confirmRemark } = payload;

  // ── Group booking → call group_booking/amend directly ────────────────────
  if (bookingData?.book_type === 'G') {
    try {
      const { access_token } = yield call(getItems, ['access_token']);
      API_PMS_HEADERS.headers.Authorization = 'Bearer ' + access_token;
      const url = `${API_URL_V1}pms/rpc/reservation/group_booking/amend`;
      const { tour_order, ...bookingDataForPayload } = bookingData as any;
      // When namelist is active ('C'), the backend forbids room modifications via amend.
      // Strip details entirely so only header fields (guests, requirements, deposits, etc.) are sent.
      const namelistActive =
        bookingData.namelist_status === 'C' || bookingData.namelist_created === true;
      const groupBookingPayload: any = {
        ...bookingDataForPayload,
        remark: tour_order ?? bookingData.remark ?? null,
        arrival: dayjs(bookingData.arrival).format('YYYY-MM-DD'),
        arrival_time: formatTimeHHmm(bookingData.arrival_time),
        departure: dayjs(bookingData.departure).format('YYYY-MM-DD'),
        departure_time: formatTimeHHmm(bookingData.departure_time),
        deposits: bookingData.deposits ?? [],
        requirements: bookingData.requirements ?? [],
      };
      if (namelistActive) {
        // Backend forbids room mutations via amend when namelist is active — strip details entirely
        delete groupBookingPayload.details;
      } else {
        groupBookingPayload.details = normalizeGroupBookingDetails(bookingData.details);
      }
      const amendBody = {
        booking_id: bookingData.booking_id,
        confirm: confirmStatus,
        confirm_remark: confirmRemark,
        booking: groupBookingPayload,
      };
      const response: ApiResponse = yield APIPostRequest(url, amendBody, API_PMS_HEADERS);
      if (response.data?.result_code === '0') {
        const processStatus = {
          status: true,
          action: 'updateBooking',
          message: response.data.result_msg,
          result_code: response.data.result_code,
          result_data: response.data.result_data,
        };
        yield put(actionCreators.reservationProcessSuccess(processStatus));
        yield delay(200);
        yield put(actionCreators.reservationProcessReset());
      } else {
        const processStatus = {
          status: false,
          action: 'updateBooking',
          message: response.data?.result_msg ?? response.data?.message ?? 'An error occurred',
          result_code: response.data?.result_code ?? 'ERROR',
        };
        yield put(actionCreators.reservationProcessSuccess(processStatus));
        yield delay(200);
        yield put(actionCreators.reservationProcessReset());
      }
    } catch (error) {
      const processStatus = {
        status: false,
        action: 'updateBooking',
        message: String(error),
        result_code: 'ERROR',
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.reservationProcessReset());
    }
    return;
  }

  const arrival = dayjs(bookingData.arrival).format('YYYY-MM-DD');
  const departure = dayjs(bookingData.departure).format('YYYY-MM-DD');
  const arrival_time = formatTimeHHmm(bookingData.arrival_time);
  const departure_time = formatTimeHHmm(bookingData.departure_time);
  const normalizedGuests = syncPrimaryGuestId(bookingData?.guests, bookingData?.guest_id);
  const booking = {
    confirm: confirmStatus,
    confirm_remark: confirmRemark,
    booking: {
      ...bookingData,
      guests: normalizedGuests ?? bookingData?.guests,
      arrival,
      departure,
      arrival_time,
      departure_time,
    },
  };
  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/reservation/booking/update`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = booking;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = {
        status: true,
        action: 'updateBooking',
        message: result_msg,
        result_code,
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.reservationProcessReset());
    } else {
      /* Show error message and signout */
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'updateBooking', message };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.reservationProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    const processStatus = { status: false, action: 'updateBooking', message: error };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.reservationProcessReset());
    yield delay(100);
  }
}
function* reservationRemoveProcess(_: actionTypes.ReservationRemove) { }

function* reservationRatePlanRequestProcess({ payload }: actionTypes.ReservationRatePlanRequest) {
  let keywords = payload;

  //
  const { systemConfig } = yield select((state: AppState) => state);
  const {
    dataSource: {
      SYSTEM: { SYSTEM_DATE },
    },
  } = systemConfig;

  // ---------- Build request params robustly ----------
  const roomCondition: any = {};
  roomCondition.booking_item = keywords.item_id || Math.floor(Math.random() * 10000000) * -1;
  roomCondition.roomtype_id = keywords.roomtype_id;

  // qty: prefer explicit qty; fall back to nights; else 1
  roomCondition.qty =
    keywords.qty !== undefined && keywords.qty !== null
      ? Number(keywords.qty)
      : keywords.nights !== undefined
        ? Number(keywords.nights)
        : 1;

  roomCondition.adult = Number(keywords.adult ?? 1);
  roomCondition.child = Number(keywords.child ?? 0);
  roomCondition.infant = Number(keywords.infant ?? 0);

  // Arrival & Departure priority:
  // 1) explicit arrival_date / departure_date
  // 2) explicit arrival / departure
  // 3) dateRange[0]/dateRange[1]
  // 4) fallback: SYSTEM_DATE and SYSTEM_DATE + nights(>=1) or +1 day
  const pickDate = (d: any) => (d ? dayjs(d).format('YYYY-MM-DD') : undefined);

  let arrivalDate =
    pickDate(keywords.arrival_date) ||
    pickDate(keywords.arrival) ||
    (keywords.dateRange?.[0] ? dayjs(keywords.dateRange[0]).format('YYYY-MM-DD') : undefined) ||
    dayjs(SYSTEM_DATE).format('YYYY-MM-DD');

  let departureDate =
    pickDate(keywords.departure_date) ||
    pickDate(keywords.departure) ||
    (keywords.dateRange?.[1] ? dayjs(keywords.dateRange[1]).format('YYYY-MM-DD') : undefined);

  if (!departureDate) {
    const nightsNum = Number(keywords.nights ?? 1);
    departureDate = dayjs(arrivalDate)
      .add(isNaN(nightsNum) ? 1 : Math.max(1, nightsNum), 'days')
      .format('YYYY-MM-DD');
  }

  roomCondition.arrival_date = arrivalDate;
  roomCondition.departure_date = departureDate;

  roomCondition.keep_original_rate = Boolean(keywords.keep_original_rate);
  roomCondition.channel_id = Number(keywords.channel_id ?? 0);
  roomCondition.origin_id = Number(keywords.origin_id ?? 0);
  roomCondition.market_id = Number(keywords.market_id ?? 0);

  // ---------- Build encoded query string safely ----------
  const params = new URLSearchParams();
  Object.entries(roomCondition).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
  });
  const queryString = params.toString();

  // console.log(queryString);

  const { access_token } = yield call(getItems, ['access_token']);
  const AccessToken = access_token;
  const url = `${API_URL_V1}pms/inventory/availableRates/?${queryString}`;
  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

  try {
    const response: ApiResponse = yield APIGetRequest(url, API_PMS_HEADERS);
    if (response.status === 200) {
      const { result_code, result_msg, result_data, msg_code } = response.data;
      // Use result_msg as primary, fallback to msg_code
      const displayMsg = String(result_msg ?? msg_code ?? '');

      if (result_code === '4') {
        localStorage.setItem('NO_AVAILABLE_RATE_MSG', displayMsg);
      } else {
        localStorage.removeItem('NO_AVAILABLE_RATE_MSG');
      }

      // Include msg_code in processStatus data so component can access it
      const processStatus = {
        status: true,
        action: 'rateplanLookup',
        message: displayMsg,
        result_code,
        data: {
          result_msg,
          msg_code,
          result_data,
        },
      };
      yield put(actionCreators.reservationRatePlanDataSouceSuccess(result_data));
      yield put(actionCreators.reservationProcessSuccess(processStatus));
    } else {
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'rateplanLookup', message };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.reservationProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    // console.log('error: ', error);

    const processStatus = {
      status: false,
      action: 'rateplanLookup',
      message: error,
    };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.reservationProcessReset());
    yield delay(100);
  }
}

/** Backend expects integer or null for these keys; normalize array/undefined to integer or null */
function normalizeIntegerParam(value: any): number | null {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) {
    const first = value[0];
    if (first == null || first === '') return null;
    const n = Number(first);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function* reservationRatePlanRequestV2Process({
  payload,
}: actionTypes.ReservationRatePlanRequestV2) {
  const { access_token } = yield call(getItems, ['access_token']);
  const AccessToken = access_token;
  const url = `${API_URL_V1}pms/rpc/inventory/get_available_rates`;
  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

  const formattedPayload = {
    ...payload,
    arrival: payload.arrival ? dayjs(payload.arrival).format('YYYY-MM-DD') : null,
    departure: payload.departure ? dayjs(payload.departure).format('YYYY-MM-DD') : null,
    market_id: normalizeIntegerParam(payload.market_id),
    origin_id: normalizeIntegerParam(payload.origin_id),
    channel_id: normalizeIntegerParam(payload.channel_id),
    roomtype_id: normalizeIntegerParam(payload.roomtype_id),
  };

  try {
    const response: ApiResponse = yield APIPostRequest(url, formattedPayload, API_PMS_HEADERS);
    if (response.status === 200) {
      const { result_code, result_msg, result_data, msg_code } = response.data;
      // Use msg_code if available (more descriptive), fallback to result_msg
      const displayMsg = String(result_msg ?? msg_code ?? '');

      if (result_code === '4') {
        localStorage.setItem('NO_AVAILABLE_RATE_MSG', displayMsg);
      } else {
        localStorage.removeItem('NO_AVAILABLE_RATE_MSG');
      }

      // Include msg_code in processStatus data so component can access it
      const processStatus = {
        status: true,
        action: 'rateplanLookup',
        message: displayMsg,
        result_code,
        data: {
          result_msg,
          msg_code,
          result_data,
        },
      };
      yield put(actionCreators.reservationRatePlanDataSouceSuccess(result_data));
      yield put(actionCreators.reservationProcessSuccess(processStatus));
    } else {
      const { message, data } = response.data;
      const processStatus = { status: false, action: 'rateplanLookup', message };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.reservationProcessReset());
      yield delay(100);
      if (response.status === 403) {
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    // console.log('error: ', error);

    const processStatus = {
      status: false,
      action: 'rateplanLookup',
      message: error,
    };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.reservationProcessReset());
    yield delay(100);
  }
}

function* reservationChangeBookingStatusProcess({
  payload,
}: actionTypes.ReservationChangeBookingStatus) {
  const changeStatusInfo = {
    booking_id: payload.booking_id,
    new_status: payload.new_status,
    remark: payload.remark,
  };


  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/reservation/booking/status/update`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = changeStatusInfo;

    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      const { result_code, result_msg } = response.data;
      const processStatus = {
        status: true,
        action: payload.action,
        message: result_msg,
        result_code,
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(200);
      yield put(actionCreators.reservationProcessReset());
    } else {
      /* Show error message and signout */
      const { message, data } = response.data || {};
      const errorMessage = message || `API returned status ${response.status}`;
      console.error('[reservationChangeBookingStatusProcess] API Error:', {
        status: response.status,
        message: errorMessage,
        data,
        response: response
      });

      const processStatus = {
        status: false,
        action: payload.action,
        message: errorMessage,
        result_code: response.data?.result_code || 'ERROR'
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield delay(100);
      yield put(actionCreators.reservationProcessReset());

      if (response.status === 403) {
        /* if data is not null force signout */
        if (data) yield put(userSignOut());
      }
    }
  } catch (error) {
    console.error('[reservationChangeBookingStatusProcess] Exception:', error);
    const errorMessage = error instanceof Error ? error.message : String(error);
    const processStatus = {
      status: false,
      action: payload.action,
      message: errorMessage,
      result_code: 'ERROR'
    };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
    yield delay(100);
    yield put(actionCreators.reservationProcessReset());
    yield delay(100);
  }
}

function* reservationBookingSummaryProcess({
  payload,
}: actionTypes.ReservationBookingSummaryRequest) {
  const arrival = dayjs(payload.arrival).format('YYYY-MM-DD');
  const departure = dayjs(payload.departure).format('YYYY-MM-DD');
  const arrival_time = dayjs(payload.arrival_time).format('HH:mm');
  const departure_time = dayjs(payload.departure_time).format('HH:mm');

  const reformData = { ...payload, arrival, departure, arrival_time, departure_time };

  const summaryInfo = { booking_data: { ...reformData } };

  try {
    const { access_token } = yield call(getItems, ['access_token']);
    const AccessToken = access_token;
    const url = `${API_URL_V1}pms/reservation/bookingsummary/`;
    API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;
    const API_DATABODY = summaryInfo;
    const response: ApiResponse = yield APIPostRequest(url, API_DATABODY, API_PMS_HEADERS);

    if (response.status === 200) {
      yield put(actionCreators.reservationBookingSummarySuccess(response.data));
    } else {
    }
  } catch (error) { }
}

function* reservationBookingCheckLockRecord({ payload }: actionTypes.ReservationLockRecord) {
  if (!payload || !payload.booking_id) return;

  const url = `${API_URL_V1}pms/reservation/booking/checklockrecord/`;
  const requestBody: any = {
    booking_id: Number(payload.booking_id),
    user_name: payload.user_name,
    session_id: String(payload.session_id),
  };

  if (payload.booking_item !== undefined) {
    requestBody.booking_item = payload.booking_item;
  }

  if (payload.block_id !== undefined) {
    requestBody.block_id = payload.block_id;
  }

  // Set Authorization header like other sagas
  const { access_token } = yield call(getItems, ['access_token']);
  const AccessToken = access_token;
  API_PMS_HEADERS.headers.Authorization = `Bearer ${AccessToken}`;

  try {
    const response: ApiResponse = yield call(APIPostRequest, url, requestBody, API_PMS_HEADERS);
    if (response.status === 200) {
      yield put(actionCreators.reservationLockRecord(response.data.result_data));
      const processStatus = { status: true, action: 'lock_record', message: 'Call lock success.' };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
    } else {
      const processStatus = { status: false, action: 'lock_record', message: 'Call lock fail.' };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
    }
  } catch (error) {
    console.error('❌ Error on check lock record:', error);
    const processStatus = { status: false, action: 'lock_record', message: String(error) };
    yield put(actionCreators.reservationProcessSuccess(processStatus));
  }
}

function* reservationBookingUnLockRecord({ payload }: actionTypes.ReservationUnlockRecord) {
  if (!payload || !payload.booking_id) return;

  const { onSuccess, onError } = payload;

  const url = `${API_URL_V1}pms/reservation/booking/unlockrecord/`;
  const requestBody: any = {
    booking_id: Number(payload.booking_id),
    user_name: payload.user_name,
    session_id: String(payload.session_id),
  };

  if (payload.booking_item !== undefined) {
    requestBody.booking_item = payload.booking_item;
  }

  if (payload.block_id !== undefined) {
    requestBody.block_id = payload.block_id;
  }

  try {
    const { access_token } = yield call(getItems, ['access_token']);
    API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;

    const response: ApiResponse = yield call(APIPostRequest, url, requestBody, API_PMS_HEADERS);
    if (response.status === 200) {
      const resultData = response.data?.result_data ?? response.data;
      yield put(actionCreators.reservationUnlockRecord(resultData));
      const processStatus = {
        status: true,
        action: 'reservationUnLockRecord',
        message: 'Call unlock success.',
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield put(actionCreators.reservationProcessReset());
      if (typeof onSuccess === 'function') {
        yield call(onSuccess, resultData);
      }
    } else {
      const processStatus = {
        status: false,
        action: 'reservationUnLockRecord',
        message: 'Call unlock fail.',
      };
      yield put(actionCreators.reservationProcessSuccess(processStatus));
      yield put(actionCreators.reservationProcessReset());
      if (typeof onError === 'function') {
        yield call(onError, response);
      }
    }
  } catch (error) {
    console.error('❌ Error on check lock record:', error);
    if (typeof onError === 'function') {
      yield call(onError, error);
    }
  }
}

function* reservationRequest() {
  yield takeEvery(actionTypes.RESERVATION_REQUEST, reservationRequestProcess);
}
function* reservationDetailRequest() {
  yield takeEvery(actionTypes.RESERVATION_DETAIL_REQUEST, reservationDetailRequestProcess);
}
function* reservationNew() {
  yield takeEvery(actionTypes.RESERVATION_NEW, reservationNewProcess);
}
function* reservationQuickNew() {
  yield takeEvery(actionTypes.RESERVATION_QUICK_NEW, reservationQuickNewProcess);
}
function* reservationUpdate() {
  yield takeEvery(actionTypes.RESERVATION_UPDATE, reservationUpdateProcess);
}
function* reservationRemove() {
  yield takeEvery(actionTypes.RESERVATION_REMOVE, reservationRemoveProcess);
}
function* reservationRatePlanRequest() {
  yield takeEvery(actionTypes.RESERVATION_RATEPLAN_REQUEST, reservationRatePlanRequestProcess);
}

function* reservationRatePlanRequestV2() {
  yield takeEvery(actionTypes.RESERVATION_RATEPLAN_REQUEST_V2, reservationRatePlanRequestV2Process);
}

function* reservationChangeBookingStatus() {
  yield takeEvery(
    actionTypes.RESERVATION_CHANGE_BOOKING_STATUS,
    reservationChangeBookingStatusProcess
  );
}
function* reservationBookingSummary() {
  yield takeEvery(
    actionTypes.RESERVATION_BOOKING_SUMMARY_REQUEST,
    reservationBookingSummaryProcess
  );
}

function* reservationLockRecord() {
  yield takeEvery(actionTypes.RESERVATION_LOCK_RECORD, reservationBookingCheckLockRecord);
}

function* reservationUnLockRecord() {
  yield takeEvery(actionTypes.RESERVATION_UNLOCK_RECORD, reservationBookingUnLockRecord);
}
export default function* rootSaga() {
  yield all([
    fork(reservationRequest),
    fork(reservationDetailRequest),
    fork(reservationNew),
    fork(reservationQuickNew),
    fork(reservationUpdate),
    fork(reservationRemove),
    fork(reservationRatePlanRequest),
    fork(reservationRatePlanRequestV2),
    fork(reservationChangeBookingStatus),
    fork(reservationBookingSummary),
    fork(reservationLockRecord),
    fork(reservationUnLockRecord),
  ]);
}
